import h from 'hyperscript';

import { createButton } from 'ui/button/button';
import { LOAD_END, LOAD_PROGRESS, LOAD_START } from 'ui/loading-event-type';
import { COMPONENT_NAME as PROGRESS_SPINNER_COMPONENT_NAME, ProgressSpinner } from 'ui/progress-spinner/progress-spinner';

import { on } from 'cadenza/utils/event-util';
import { assert, assertUnreachable } from 'cadenza/utils/custom-error';
import { entries } from 'cadenza/utils/type-util';

import './loading-state-cover.css';
import i18n from './loading-state-cover.properties';

interface State {
  cancelable: boolean;
  loadingState: LoadingState | typeof DEFAULT_LOADING_STATE;
  userMessage: string;
  showOverlay: boolean;
}

type StateToUi = {
  [Property in keyof State]: (value: State[Property]) => void;
};

const COMPONENT_NAME = 'd-loading-state-cover';
const LONG_LOADING_TIME_THRESHOLD = 1000; // ms until the loading time is considered to take "long"

export type LoadingState = 'DEFAULT' | 'LOADING' | 'LOADING_LONG' | 'LOADING_COMPLETE' | 'CANCELED' | 'OUTDATED';

export const STATE_CHANGED_EVENT = 'stateChangedEvent';
const EVENT_BUS_ELEMENT_NAME = 'event-bus-element-query';

const DEFAULT_CANCELABLE = false;
const DEFAULT_LOADING_STATE: LoadingState = 'DEFAULT';
const DEFAULT_USER_MESSAGE = '';
const DEFAULT_SHOW_OVERLAY = false;

// exported for testing ONLY
export const CSS_CLASSES = {
  CANCEL_BUTTON: 'cancel-button',
  REFRESH_BUTTON: 'refresh-button',
  LOADING_SPINNER: PROGRESS_SPINNER_COMPONENT_NAME,
  USER_MESSAGE: 'user-message',
  OVERLAY: `${COMPONENT_NAME}--overlay`
};

export interface LoadingStateDelegate {
  /** Called to cancel current view rendering */
  cancelLoading: () => void;
  /** Called to force the view to refresh */
  refresh: () => void;
}
export type ViewWithLoadingState = HTMLElement & LoadingStateDelegate;

export interface LoadingStateOptions {
  /** Whether the view should be dimmed by an overlay while loading, default: `true` */
  enabled?: boolean;
  /** transition right back to default when using the cancel button, skipping 'outdated' state. */
  overlayEnabled?: boolean;
  /** If true, skip outdated state transition. */
  skipOutdatedState?: boolean;
}

const DEFAULT_OPTIONS = {
  enabled: true,
  overlayEnabled: true,
  skipOutdatedState: false
};

/**
 * Show a spinner and a message to the user indicating the current state of the document: is it
 * currently loading, if the data is outdated and so on. When specified, the user can cancel the
 * loading of the view and reload it.
 */
export class LoadingStateCover extends HTMLElement {

  readonly #skipOutdatedState: boolean;
  readonly #overlay: HTMLElement;
  readonly #userMessage: HTMLElement;
  readonly #progressSpinner: ProgressSpinner;
  readonly #refreshButton: HTMLButtonElement;
  readonly #cancelButton: HTMLButtonElement;
  readonly #viewStateIndicator: HTMLElement;

  #enabled: boolean;
  #overlayEnabled: boolean;

  /**
   * @param view View connected to the loading state cover
   * @param options {LoadingStateOptions} Options {@see LoadingStateOptions}
   */
  constructor (
    view: ViewWithLoadingState,
    options: LoadingStateOptions) {
    super();
    const _options = {
      ...DEFAULT_OPTIONS,
      ...options
    };
    this.#enabled = _options.enabled;
    this.#overlayEnabled = _options.overlayEnabled ?? DEFAULT_OPTIONS.overlayEnabled;
    this.#skipOutdatedState = _options.skipOutdatedState ?? DEFAULT_OPTIONS.skipOutdatedState;

    this.#overlay = h(`.${CSS_CLASSES.OVERLAY}`, { hidden: true });
    this.#userMessage = h(`span.ellipsis.${CSS_CLASSES.USER_MESSAGE}`);
    this.#progressSpinner = new ProgressSpinner({ label: this.#userMessage, size: 'xs' });
    this.#refreshButton = createButton(i18n('button.update'), {
      size: 's',
      styleClass: CSS_CLASSES.REFRESH_BUTTON,
      onclick: () => {
        this.resetLoadingStateToDefault();
        view.refresh();
      }
    });
    this.#cancelButton = createButton(i18n('button.cancel'), {
      size: 's',
      styleClass: CSS_CLASSES.CANCEL_BUTTON,
      onclick: () => {
        this.#setStateToCanceled();
        view.cancelLoading();
      }
    });
    this.#viewStateIndicator = h('.d-loading-state-cover--indicator.d-alert.d-alert-opaque.d-alert-info', [
      this.#progressSpinner, this.#userMessage, this.#cancelButton, this.#refreshButton ]);

    const eventBus = getEventBus(view, this.getAttribute(EVENT_BUS_ELEMENT_NAME) ?? undefined);
    this.#initEventListeners(eventBus);
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this.append(this.#overlay, this.#viewStateIndicator);
    this._state.init();
  }

  get overlayEnabled (): boolean {
    return this.#overlayEnabled;
  }

  set overlayEnabled (value: boolean) {
    this.#overlayEnabled = value;
  }

  /**
   * Sets the internal state of the component to outdated. The user will then be able to trigger a
   * refresh of the view.
   */
  setOutdated () {
    this._state.set({
      loadingState: 'OUTDATED',
      userMessage: i18n('outdatedData'),
      showOverlay: this.#overlayEnabled
    });
  }

  /**
   * Gets the current loading state of the view.
   *
   * @return {LoadingState} loading state
   */
  getLoadingState (): LoadingState {
    return this._state.get('loadingState') as LoadingState;
  }

  get enabled () {
    return this.#enabled;
  }

  set enabled (value) {
    this.#enabled = value;
  }

  #initEventListeners (eventBus: EventTarget) {
    on(eventBus, LOAD_START, (event: CustomEvent) => {
      if (!this.enabled) {
        return;
      }
      if (this.#isOutdated()) {
        this.#progressSpinner.hidden = false;
        return;
      }
      this.#setStateToLoading(event.detail?.cancelable ?? DEFAULT_CANCELABLE);
    });
    on(eventBus, LOAD_END, () => {
      if (!this.enabled) {
        return;
      }
      if (this.#isCanceled() || this.#isOutdated()) {
        this.#progressSpinner.hidden = true;
        return;
      }
      this.#setStateToLoadEnd();
    });
    on(eventBus, LOAD_PROGRESS, (event: CustomEvent) => {
      if (!this.enabled) {
        return;
      }
      this._state.set({
        cancelable: event.detail?.cancelable ?? DEFAULT_CANCELABLE
      });
    });
  }

  _state = (() => {
    const state: State = {
      cancelable: DEFAULT_CANCELABLE,
      loadingState: DEFAULT_LOADING_STATE,
      userMessage: DEFAULT_USER_MESSAGE,
      showOverlay: DEFAULT_SHOW_OVERLAY
    };

    // Describes how the UI is updated in response to state changes. Don't modify the UI anywhere
    // else to encapsulate that (besides the initial appending of elements).
    const stateToUi: StateToUi = {
      cancelable: (cancelable) => {
        if (state.loadingState === 'LOADING_LONG') {
          this.#cancelButton.hidden = !cancelable;
        }
      },
      userMessage: (msg) => {
        this.#userMessage.textContent = msg;
      },
      showOverlay: (shouldShowOverlay) => {
        this.#overlay.hidden = !shouldShowOverlay;
      },
      loadingState: (loadingState) => {
        this.dataset.state = loadingState;

        switch (loadingState) {
          case 'LOADING':
            setTimeout(() => {
              // If we are still loading after a certain amount of time, it's considered to be a
              // long time.
              if (state.loadingState === 'LOADING') {
                this.#setStateToLongLoading();
              }
            }, LONG_LOADING_TIME_THRESHOLD);
            this.hidden = false;
            this.#cancelButton.hidden = true;
            this.#overlay.hidden = !state.showOverlay;
            this.#progressSpinner.hidden = false;
            this.#refreshButton.hidden = true;
            break;

          case 'LOADING_LONG':
            this.#cancelButton.hidden = !state.cancelable;
            break;

          case 'CANCELED':
            this.#cancelButton.hidden = true;
            this.#overlay.hidden = !state.showOverlay;
            this.#refreshButton.hidden = false;
            break;

          case 'OUTDATED':
            this.hidden = false;
            this.#cancelButton.hidden = true;
            this.#overlay.hidden = !state.showOverlay;
            this.#progressSpinner.hidden = true;
            this.#refreshButton.hidden = false;
            break;

          case 'DEFAULT':
            this.hidden = true;
            break;

          case 'LOADING_COMPLETE':
            this.hidden = true;
            break;

          default:
            assertUnreachable(loadingState);
        }
        this.dispatchEvent(new CustomEvent(STATE_CHANGED_EVENT, { detail: { loadingState } }));
      }
    };
    return {
      init: () => {
        entries(state).forEach(([ key, value ]) => stateToUi[key](value as never));
      },
      set: (newState: Partial<State>) => {
        entries(newState as State).map(([ key, value ]) => {
          if (state[key] !== value) {
            state[key] = value as never;
            stateToUi[key](value as never);
          }
        });
      },
      get: (propName: keyof State) => state[propName]
    };
  })();

  #setStateToLoading (cancelable: boolean) {
    this._state.set({
      cancelable,
      userMessage: i18n('loading'),
      loadingState: 'LOADING',
      showOverlay: this.#overlayEnabled
    });
  }

  #setStateToLoadEnd () {
    this._state.set({
      cancelable: DEFAULT_CANCELABLE,
      userMessage: DEFAULT_USER_MESSAGE,
      loadingState: 'LOADING_COMPLETE',
      showOverlay: false
    });
  }

  #setStateToLongLoading () {
    this._state.set({
      userMessage: i18n('loadingLong'),
      loadingState: 'LOADING_LONG'
    });
  }

  #setStateToCanceled () {
    if (this.#skipOutdatedState) {
      this.resetLoadingStateToDefault();
    } else {
      // Progress to outdated state
      this._state.set({
        userMessage: i18n('outdatedData'),
        loadingState: 'CANCELED'
      });
    }
  }

  resetLoadingStateToDefault () {
    this._state.set({
      loadingState: DEFAULT_LOADING_STATE,
      showOverlay: false
    });
  }

  resetOutdatedState () {
    if (this.#isOutdated()) {
      this._state.set({
        loadingState: DEFAULT_LOADING_STATE,
        showOverlay: false
      });
    }
  }

  #isCanceled () {
    return this._state.get('loadingState') === 'CANCELED';
  }

  #isOutdated () {
    return this._state.get('loadingState') === 'OUTDATED';
  }

  // for testing ONLY
  exposeInternalState (propName: keyof State) {
    return this._state.get(propName);
  }

}

function getEventBus (eventBus?: EventTarget, eventBusElementQuery?: string) {
  if (eventBus) {
    return eventBus;
  } else {
    assert(eventBusElementQuery, 'Make sure to define the "event-bus-element-query" attribute or pass in the eventBus');
    const elementEventBus = document.querySelector(eventBusElementQuery);
    assert(elementEventBus, 'Event bus cannot be null');
    return elementEventBus;
  }
}

customElements.define(COMPONENT_NAME, LoadingStateCover);
